import { SEO } from 'app/components/SEO'
import { Props as StructuredDataProps } from 'app/components/StructuredData'
import { Props as FooterProps } from 'app/containers/Footer'
import { Props as HeaderProps } from 'app/containers/Header'
import { PageProps } from 'gatsby'
import React, { memo, Suspense } from 'react'

const Header = lazyComponent(() => import('app/containers/Header'))
const Footer = lazyComponent(() => import('app/containers/Footer'))
const Hero = lazyComponent(() => import('app/components/Hero/Simple'))
const Intro = lazyComponent(() => import('app/components/Intro/Three'))
const Paragraph = lazyComponent(() => import('app/components/Paragraph'))
const SlidingText = lazyComponent(() => import('app/components/SlidingText'))
const VisualChef = lazyComponent(() => import('app/components/Visual/Chef'))
const VisualService = lazyComponent(
  () => import('app/components/Visual/Service'),
)
const ZigZag = lazyComponent(() => import('app/components/ZigZag'))
const BlockCTA = lazyComponent(() => import('app/components/BlockCTA'))
const Mosaic = lazyComponent(() => import('app/components/Gallery/Mosaic'))
const BannerCTA = lazyComponent(() => import('app/components/BannerCTA'))
const StructuredData = lazyComponent(
  () => import('app/components/StructuredData'),
)
const Info = lazyComponent(() => import('app/components/Info'))

import { lazyComponent } from 'app/utils/customFunctions'

import { Props } from './contents'

export interface PageContext {
  id: string
  languageCode: string
  languagePrefix: string | null
  headerProps?: HeaderProps
  footerProps?: FooterProps
  structuredDataProps?: StructuredDataProps
}

export interface Context extends PageContext {
  props: Props
}

export const Head = ({ pageContext }: PageProps<void, Context>) => {
  const context = pageContext as any

  return <SEO {...context.seoProps} />
}

export default memo(function RestaurantPageTemplate({
  pageContext,
}: PageProps<void, Context>) {
  const context = pageContext as any

  return (
    <Suspense fallback={null}>
      {context.headerProps ? (
        <Header
          languageCode={context.languageCode}
          languagePrefix={context.languagePrefix}
          pageID={context.id}
          bookingID={context.blockCTAProps.ID}
          flashNewsProps={context.popupProps ? context.popupProps : null}
          {...context.headerProps}
        />
      ) : null}
      {context.heroProps ? (
        <Hero variant="widget" {...context.heroProps} />
      ) : null}
      {context.introProps ? <Intro {...context.introProps} /> : null}
      {context.paragraphProps ? (
        <Paragraph {...context.paragraphProps} />
      ) : null}
      {context.visualServiceProps ? (
        <VisualService variant="compact" {...context.visualServiceProps} />
      ) : null}
      {context.visualChefProps ? (
        <VisualChef {...context.visualChefProps} />
      ) : null}
      {context.zigZagProps ? <ZigZag {...context.zigZagProps} /> : null}
      {context.slidingTextProps ? (
        <SlidingText {...context.slidingTextProps} />
      ) : null}
      {context.infoProps ? <Info {...context.infoProps} /> : null}
      {context.blockCTAProps ? <BlockCTA {...context.blockCTAProps} /> : null}
      {context.mosaicProps ? <Mosaic {...context.mosaicProps} /> : null}
      {context.bannerCTAProps ? (
        <BannerCTA {...context.bannerCTAProps} />
      ) : null}
      {context.footerProps ? (
        <Footer languageCode={context.languageCode} {...context.footerProps} />
      ) : null}

      {context.structuredDataProps ? (
        <StructuredData
          languageCode={pageContext.languageCode}
          {...context.structuredDataProps}
        />
      ) : null}
    </Suspense>
  )
})
